import { formatHourMinutes, validateHourRanges } from '@repo/design-system';
import type { AuthoredOpeningHoursDay } from '@repo/frontend-types';
import {
  dayName,
  nextWeekday,
  toMinutes,
  toTrackRanges,
  WEEKDAYS,
  type Weekday,
} from './hoursModel';

/**
 * The rules the Opening hours step applies to a whole week before it saves one
 * (spec twin-details-page-hours-week, R6). The day rules are the design system's
 * `validateHourRanges`, which mirrors the store's per-day refusals; the rule ACROSS days lives here,
 * because the hour track checks one day at a time.
 *
 * `findSpillClash` is a copy of the store's within-version rule (`validateVersionShape`,
 * `apps/twin-middleware/src/opening-hours/opening-hours-schedule.ts`): two copies by necessity, since
 * the middleware imports `@repo/frontend-types` as types only, held equal by one case file both test
 * suites read (`packages/frontend-types/fixtures/opening-hours-week-spill-cases.json`).
 */

export interface SpillClash {
  day: Weekday;
  next: Weekday;
  /** `HH:mm` on the next day. */
  spill: string;
  /** `HH:mm`: where the next day's own hours start. */
  opens: string;
}

/** The latest close a day carries into the next morning, in minutes after that morning's midnight. */
const spillOf = (day: AuthoredOpeningHoursDay | undefined): number | null => {
  if (!day || day.state !== 'ranges') return null;
  const spills = day.ranges
    .filter(r => r.closesNextDay)
    .map(r => toMinutes(r.close))
    // A midnight close carries nothing into the next day.
    .filter(m => m > 0);
  return spills.length === 0 ? null : Math.max(...spills);
};

/** Where a day's own hours start: none when Closed, midnight when open 24 hours. */
const firstStartOf = (day: AuthoredOpeningHoursDay | undefined): number | null => {
  if (!day || day.state === 'closed') return null;
  if (day.state === 'open_24h') return 0;
  return day.ranges.length === 0 ? null : Math.min(...day.ranges.map(r => toMinutes(r.open)));
};

/**
 * The first day whose past-midnight spill runs past the next day's own opening, or null. Sunday wraps
 * to Monday; a spill ending exactly when the next day opens is allowed; a Closed next day takes any
 * spill; a 24-hour next day opens at 00:00.
 */
export const findSpillClash = (days: readonly AuthoredOpeningHoursDay[]): SpillClash | null => {
  for (const day of WEEKDAYS) {
    const spill = spillOf(days.find(d => d.dayOfWeek === day));
    const next = nextWeekday(day);
    const opens = firstStartOf(days.find(d => d.dayOfWeek === next));
    if (spill !== null && opens !== null && spill > opens) {
      return { day, next, spill: formatHourMinutes(spill), opens: formatHourMinutes(opens) };
    }
  }
  return null;
};

/**
 * Why a proposed week cannot be saved, as the sentence the step shows and announces — or null when
 * it can. A day breaking its own rules is named with the hour track's words; a spill across days is
 * R6.4's *Not changed: …* sentence.
 */
export const weekRefusal = (days: readonly AuthoredOpeningHoursDay[]): string | null => {
  for (const d of days) {
    if (d.state !== 'ranges') continue;
    if (d.ranges.length === 0)
      return `Not changed: ${dayName(d.dayOfWeek)} is open but has no hours.`;
    const problem = validateHourRanges(toTrackRanges(d));
    if (problem) return `Not changed: on ${dayName(d.dayOfWeek)}, ${problem}.`;
  }
  const clash = findSpillClash(days);
  return clash === null
    ? null
    : `Not changed: ${dayName(clash.day)}’s hours would run to ${clash.spill} on ${dayName(
        clash.next,
      )}, when ${dayName(clash.next)} opens at ${clash.opens}.`;
};
